import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PromoBanner from "../../components/PromoBanner";
import { ArrowBigUp, Sparkles, Plus, CheckCircle2, Clock, XCircle, Rocket, Send } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  idea: { label: "Idea", color: "#9aa0aa", Icon: Sparkles },
  planned: { label: "Planned", color: "#7aa2ff", Icon: Clock },
  in_progress: { label: "In Progress", color: "#ffd166", Icon: Rocket },
  released: { label: "Released", color: "var(--bull)", Icon: CheckCircle2 },
  declined: { label: "Declined", color: "#ff6b6b", Icon: XCircle },
};

function RequestRow({ req, onVote, isAdmin, onStatus }) {
  const [busy, setBusy] = useState(false);
  const [votes, setVotes] = useState(req.upvotes || 0);
  const [voted, setVoted] = useState(false);
  const meta = STATUS_META[req.status] || STATUS_META.idea;

  const doVote = async () => {
    if (busy) return; setBusy(true);
    try {
      const res = await onVote(req.id, voted ? 0 : 1);
      setVotes(res.upvotes);
      setVoted(res.your_vote === 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vote failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-surface p-4 flex items-start gap-3" data-testid={`fr-row-${req.id}`}>
      <button onClick={doVote}
              className={`flex flex-col items-center px-2 py-1.5 rounded-lg transition-colors ${
                voted
                  ? "bg-[var(--bull)]/15 border border-[var(--bull)]/40 text-[var(--bull)]"
                  : "border border-white/10 text-white/60 hover:text-white hover:border-white/25"
              }`}
              data-testid={`fr-vote-${req.id}`}>
        <ArrowBigUp className="w-4 h-4" fill={voted ? "currentColor" : "none"} />
        <span className="font-mono text-[11px] tabular-nums font-semibold mt-0.5">{votes}</span>
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border"
            style={{ borderColor: `${meta.color}55`, color: meta.color, background: `${meta.color}15` }}
            data-testid={`fr-status-${req.id}`}
          >
            <meta.Icon className="w-3 h-3" /> {meta.label}
          </div>
          <div className="text-[11px] label-mono">
            by @{req.author_username}
          </div>
        </div>
        <div className="mt-1.5 text-white text-[15px] font-semibold tracking-tight leading-snug">
          {req.title}
        </div>
        <div className="mt-1 text-white/70 text-[13px] leading-relaxed whitespace-pre-wrap">
          {req.body}
        </div>
        {req.admin_notes && (
          <div className="mt-2 text-[12px] text-[#ffd166] italic border-l-2 border-[#ffd166]/40 pl-2">
            <span className="label-mono not-italic">Team note:</span> {req.admin_notes}
          </div>
        )}
        {isAdmin && (
          <div className="mt-2.5 flex items-center gap-1 flex-wrap">
            {Object.entries(STATUS_META).map(([id, m]) => (
              <button key={id} onClick={() => onStatus(req.id, id)}
                      className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded border transition ${
                        req.status === id
                          ? "text-white"
                          : "text-white/50 hover:text-white"
                      }`}
                      style={req.status === id
                        ? { borderColor: `${m.color}55`, background: `${m.color}22`, color: m.color }
                        : { borderColor: "rgba(255,255,255,0.10)" }}
                      data-testid={`fr-set-${req.id}-${id}`}>
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function FeatureRequests() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("top");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const q = new URLSearchParams();
      if (statusFilter) q.set("status", statusFilter);
      q.set("sort", sort);
      const { data } = await api.get(`/api/community/feature-requests?${q}`);
      setItems(data.requests || []);
    } catch (err) { console.warn("[FR] load failed:", err); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, sort]);

  const submit = async () => {
    if (form.title.trim().length < 5) { toast.error("Title too short"); return; }
    setBusy(true);
    try {
      await api.post("/api/community/feature-requests", form);
      toast.success("Submitted");
      setForm({ title: "", body: "" });
      setCreating(false);
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || d || "Failed");
    } finally { setBusy(false); }
  };

  const doVote = async (id, direction) =>
    (await api.post(`/api/community/feature-requests/${id}/vote`, { direction })).data;

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/api/community/feature-requests/${id}/status`, { status });
      toast.success("Status updated");
      await load();
    } catch (err) { toast.error("Status update failed"); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="feature-requests-page">
      <Header />
      <PromoBanner />
      <main id="main-content" className="flex-1 max-w-[1000px] w-full mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
          <div>
            <div className="label-mono">Roadmap</div>
            <h1 className="text-3xl sm:text-4xl text-white font-semibold tracking-tight mt-1">Feature Requests</h1>
            <p className="text-white/60 text-[13px] mt-1 max-w-xl">
              Suggest what to build next. Upvote what matters. The top requests each month go straight to the FlowFinder team.
            </p>
          </div>
          {user && (
            <button onClick={() => setCreating((v) => !v)}
                    className="btn-accent inline-flex items-center gap-2"
                    data-testid="fr-new-btn">
              <Plus className="w-3.5 h-3.5" /> New request
            </button>
          )}
        </div>

        {creating && (
          <div className="card-surface p-4 mb-3" data-testid="fr-composer">
            <div className="space-y-2">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="Short, descriptive title"
                     className="input-glass w-full" maxLength={200}
                     data-testid="fr-title-input"/>
              <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                        placeholder="Describe the feature, who benefits and how you'd use it."
                        rows={4} className="input-glass w-full" maxLength={5000}
                        data-testid="fr-body-input"/>
              <div className="flex justify-end gap-2">
                <button onClick={() => setCreating(false)} className="btn-ghost">Cancel</button>
                <button onClick={submit} disabled={busy}
                        className="btn-accent inline-flex items-center gap-2"
                        data-testid="fr-submit-btn">
                  <Send className="w-3.5 h-3.5" /> Submit
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {["", "idea", "planned", "in_progress", "released", "declined"].map((s) => {
            const meta = s === "" ? { label: "All", color: "#ffffff" } : STATUS_META[s];
            return (
              <button key={s || "all"} onClick={() => setStatusFilter(s)}
                      className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider rounded-full border ${
                        statusFilter === s
                          ? "bg-white/[0.08] border-white/30 text-white"
                          : "bg-white/[0.02] border-white/10 text-white/60 hover:text-white"
                      }`}
                      data-testid={`fr-filter-${s || 'all'}`}>
                {meta.label}
              </button>
            );
          })}
          <select value={sort} onChange={(e) => setSort(e.target.value)}
                  className="input-glass py-1 text-[11px] ml-auto"
                  data-testid="fr-sort-select">
            <option value="top">Top</option>
            <option value="new">New</option>
          </select>
        </div>

        <div className="space-y-2">
          {items.length === 0 && (
            <div className="text-white/40 text-sm text-center py-8">No feature requests yet — be the first!</div>
          )}
          {items.map((r) => (
            <RequestRow key={r.id} req={r} onVote={doVote} isAdmin={isAdmin} onStatus={setStatus} />
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
